import React, { useState } from 'react';

function fmt(v, decimals = 0) {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtK(v) {
  const n = parseFloat(v) || 0;
  if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1000) return (n / 1000).toFixed(0) + 'K';
  return fmt(n);
}

export default function CashView({ cashflow, entity }) {
  const [view, setView] = useState('weekly'); // weekly | receipts | payments

  if (!cashflow || !cashflow.weeks || cashflow.weeks.length === 0) {
    return (
      <div className="view cash-view">
        <div className="empty-state">
          <div className="empty-icon">💰</div>
          <h2>No cashflow data loaded</h2>
          <p>Upload the Cashflow Excel file to see the weekly forward view.</p>
          <p className="empty-files">Expected file: <code>CASHFLOW.xlsx</code></p>
        </div>
      </div>
    );
  }

  const { weeks, receipts, payments, openingBalance, closingBalance, currency } = cashflow;
  const cur = (v) => `${currency} ${fmt(v)}`;
  const curK = (v) => `${currency} ${fmtK(v)}`;

  // Find weeks with issues
  const dangerWeeks = weeks.filter(w => w.isNegative);
  const tightWeeks = weeks.filter(w => w.isTight && !w.isNegative);

  // Total receipts and payments expected
  const totalReceipts = weeks.reduce((s, w) => s + w.receipts, 0);
  const totalPayments = weeks.reduce((s, w) => s + w.payments, 0);

  return (
    <div className="view cash-view">

      {/* KPI strip */}
      <div className="kpi-row" style={{ marginBottom: 20 }}>
        <div className="kpi">
          <span className="kpi-label">Current Balance</span>
          <span className="kpi-value">{cur(openingBalance)}</span>
          <span className="kpi-sub">{currency} opening</span>
        </div>
        <div className={`kpi ${closingBalance < 0 ? 'danger' : closingBalance < 100000 ? 'warn' : 'good'}`}>
          <span className="kpi-label">End of Period</span>
          <span className="kpi-value">{cur(closingBalance)}</span>
          <span className="kpi-sub">projected closing</span>
        </div>
        <div className="kpi good">
          <span className="kpi-label">Expected In</span>
          <span className="kpi-value">{curK(totalReceipts)}</span>
          <span className="kpi-sub">next 12 weeks</span>
        </div>
        <div className="kpi warn">
          <span className="kpi-label">Expected Out</span>
          <span className="kpi-value">{curK(totalPayments)}</span>
          <span className="kpi-sub">next 12 weeks</span>
        </div>
        {dangerWeeks.length > 0 && (
          <div className="kpi danger">
            <span className="kpi-label">Cash Crunch</span>
            <span className="kpi-value">{dangerWeeks.length} week{dangerWeeks.length > 1 ? 's' : ''}</span>
            <span className="kpi-sub">negative balance projected</span>
          </div>
        )}
      </div>

      {/* Alerts */}
      {dangerWeeks.length > 0 && (
        <div className="cash-alert danger">
          🔴 <strong>Negative balance projected</strong> in: {dangerWeeks.map(w => w.label).join(', ')} — accelerate collections immediately
        </div>
      )}
      {tightWeeks.length > 0 && dangerWeeks.length === 0 && (
        <div className="cash-alert warn">
          🟡 <strong>Tight cash</strong> in: {tightWeeks.map(w => w.label).join(', ')} — monitor closely
        </div>
      )}

      {/* View toggle */}
      <div className="toggle-group" style={{ marginBottom: 16 }}>
        <button className={`toggle-btn ${view === 'weekly' ? 'active' : ''}`} onClick={() => setView('weekly')}>Weekly View</button>
        <button className={`toggle-btn ${view === 'receipts' ? 'active' : ''}`} onClick={() => setView('receipts')}>Expected Receipts</button>
        <button className={`toggle-btn ${view === 'payments' ? 'active' : ''}`} onClick={() => setView('payments')}>Planned Payments</button>
      </div>

      {/* Weekly cashflow table */}
      {view === 'weekly' && (
        <div className="table-wrap">
          <table className="data-table cash-table">
            <thead>
              <tr>
                <th>Week</th>
                <th className="right">Opening</th>
                <th className="right good-col">Receipts</th>
                <th className="right warn-col">Payments</th>
                <th className="right">Net</th>
                <th className="right">Closing</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w, i) => (
                <tr key={i} className={w.isNegative ? 'row-danger' : w.isTight ? 'row-warn' : ''}>
                  <td style={{ fontWeight: 600 }}>{w.label}</td>
                  <td className="right" style={{ color: '#64748b' }}>{w.opening > 0 ? curK(w.opening) : '—'}</td>
                  <td className="right good">{w.receipts > 0 ? curK(w.receipts) : '—'}</td>
                  <td className="right danger">{w.payments > 0 ? curK(w.payments) : '—'}</td>
                  <td className={`right ${w.net >= 0 ? 'good' : 'danger'}`} style={{ fontWeight: 600 }}>
                    {w.receipts > 0 || w.payments > 0 ? (w.net >= 0 ? '+' : '') + curK(w.net) : '—'}
                  </td>
                  <td className={`right ${w.isNegative ? 'danger' : w.isTight ? 'warn' : ''}`} style={{ fontWeight: 600 }}>
                    {w.closing > 0 ? curK(w.closing) : w.closing < 0 ? curK(w.closing) : '—'}
                    {w.isNegative && ' ⚠'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Expected receipts */}
      {view === 'receipts' && (
        <div>
          {receipts.length === 0 ? (
            <div className="empty-state small"><p>No receipt data available.</p></div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th className="right">Outstanding</th>
                    {weeks.slice(0, 8).map((w, i) => (
                      <th key={i} className="right" style={{ fontSize: 11 }}>{w.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{r.name}</td>
                      <td className="right bold">{curK(r.total)}</td>
                      {(r.weekly || []).slice(0, 8).map((v, j) => (
                        <td key={j} className={`right ${v > 0 ? 'good' : ''}`} style={{ fontSize: 12 }}>
                          {v > 0 ? curK(v) : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Planned payments */}
      {view === 'payments' && (
        <div>
          {payments.length === 0 ? (
            <div className="empty-state small"><p>No payment data available.</p></div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Vendor / Payee</th>
                    <th className="right">Total Due</th>
                    {weeks.slice(0, 8).map((w, i) => (
                      <th key={i} className="right" style={{ fontSize: 11 }}>{w.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{p.name}</td>
                      <td className="right bold danger">{curK(p.total)}</td>
                      {(p.weekly || []).slice(0, 8).map((v, j) => (
                        <td key={j} className={`right ${v > 0 ? 'danger' : ''}`} style={{ fontSize: 12 }}>
                          {v > 0 ? curK(v) : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
